import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import { ApiResponse } from '../../../types/commons';
import { HttpStatus } from '../../utils/http-status.util';
import { SKIP_GLOBAL_ERROR_REDIRECT } from '../../utils/http-context-tokens.util';
import {
  CONVERSION_FORBIDDEN_CODE,
  CONVERSION_NOT_FOUND_CODE,
  SUPERADMIN_MODULE_FORBIDDEN_CODE,
} from '../../utils/conversion-error-codes.util';
import { RuntimeConfigService } from '../common/runtime-config.service';

/**
 * What the exists check concluded.
 *
 * - `exists` — usable and the caller may open it.
 * - `not-found` — missing, soft-deleted, or still awaiting its files (`RESP`).
 * - `forbidden` — a real, finished conversion that belongs to someone else (`PERM`).
 * - `inconclusive` — the check itself could not answer (offline, timeout, 5xx,
 *   an unexpected status). NOT evidence about the conversion: the caller must
 *   carry on, and the page's own request will report its own failure.
 */
export type ConversionExistsVerdict = 'exists' | 'not-found' | 'forbidden' | 'inconclusive';

/** A verdict plus the transport detail behind it, for the console record. */
export interface ConversionExistsResult {
  verdict: ConversionExistsVerdict;
  /** HTTP status, or `null` when the request never produced one. */
  httpStatus: number | null;
  /** The backend's `errorCode`, when it sent one. */
  errorCode: string | null;
}

/**
 * Asks the backend one question — "may this user open this conversion?" —
 * without loading the conversion. Used by `conversionExistsGuard` so a dead or
 * foreign conversion is refused BEFORE the filing page mounts.
 *
 * ## Why this never throws
 *
 * Every failure is folded into a verdict. A guard that errored would cancel the
 * navigation on a flaky check, locking a user out of a perfectly valid filing;
 * `inconclusive` makes fail-open the default and the only way to block is a
 * definite answer from the server.
 *
 * ## 401
 *
 * Deliberately `inconclusive`. `isAuthenticatedGuard` already ran above this
 * guard on the same navigation, and `sessionExpiryInterceptor` owns the
 * forced-logout path for a 401 on an authenticated call — this service must not
 * second-guess it.
 */
@Injectable({ providedIn: 'root' })
export class ConversionExistsService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RuntimeConfigService);

  private existsUrl(conversionId: string): string {
    return `${this.config.apiUrl()}/v2/mbrs/conversions/${encodeURIComponent(conversionId)}/exists`;
  }

  check(conversionId: string): Observable<ConversionExistsResult> {
    return this.http
      .get<ApiResponse<boolean>>(this.existsUrl(conversionId), {
        withCredentials: true,
        // The guard classifies and toasts this itself; the global handler's
        // redirect to the error page must not win that race.
        context: new HttpContext().set(SKIP_GLOBAL_ERROR_REDIRECT, true),
      })
      .pipe(
        map((): ConversionExistsResult => ({ verdict: 'exists', httpStatus: HttpStatus.Ok, errorCode: null })),
        catchError((error: unknown) => of(this.toResult(error))),
      );
  }

  private toResult(error: unknown): ConversionExistsResult {
    if (!(error instanceof HttpErrorResponse)) {
      return { verdict: 'inconclusive', httpStatus: null, errorCode: null };
    }
    const errorCode = typeof error.error?.errorCode === 'string' ? error.error.errorCode : null;
    const httpStatus = error.status;

    // The `errorCode` is the ONLY thing that blocks. A bare 404 or 403 is NOT
    // enough: a route that does not exist (this frontend reaching a backend
    // that has no exists endpoint, a proxy answering for it) is also a 404
    // with no code, and reading that as "no such conversion" would refuse
    // every filing. A refusal needs the backend to have said so by name; the
    // page's own request still reports a genuinely missing conversion.
    if (errorCode === CONVERSION_NOT_FOUND_CODE) {
      return { verdict: 'not-found', httpStatus, errorCode };
    }
    // A SuperAdmin may not use conversions at all. Only reached if
    // `canUseConversionsGuard` was bypassed; blocking is correct, and
    // `superAdminModuleForbiddenInterceptor` moves them to User Management.
    if (errorCode === CONVERSION_FORBIDDEN_CODE || errorCode === SUPERADMIN_MODULE_FORBIDDEN_CODE) {
      return { verdict: 'forbidden', httpStatus, errorCode };
    }
    return { verdict: 'inconclusive', httpStatus, errorCode };
  }
}
